import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.casagatchalian.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication endpoints
export const authAPI = {
  // Public endpoints
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  
  // Protected endpoints
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  
  // Admin only
  createStaff: (data) => api.post('/auth/staff/create', data),
};

// Menu Items endpoints
export const menuAPI = {
  getAll: (params) => api.get('/menu-items', { params }),
  getById: (id) => api.get(`/menu-items/${id}`),
  create: (data) => api.post('/menu-items', data),
  update: (id, data) => api.put(`/menu-items/${id}`, data),
  delete: (id) => api.delete(`/menu-items/${id}`),
};

// Orders endpoints
export const orderAPI = {
  getAll: (params) => api.get('/orders', { params }), // Admin/Staff only
  getMyOrders: (params) => api.get('/my-orders', { params }), // Customer's own orders
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  updateStatus: (id, status) => api.put(`/orders/${id}`, { status }),
  delete: (id) => api.delete(`/orders/${id}`),
};

// Bookings endpoints
export const bookingAPI = {
  getAll: (params) => api.get('/bookings', { params }), // Admin/Staff only
  getMyBookings: (params) => api.get('/my-bookings', { params }), // Customer's own bookings
  getById: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  update: (id, data) => api.put(`/bookings/${id}`, data),
  updateStatus: (id, status) => api.put(`/bookings/${id}`, { status }),
  delete: (id) => api.delete(`/bookings/${id}`),
};

// Event Bookings endpoints
export const eventBookingAPI = {
  getAll: (params) => api.get('/event-bookings', { params }), // Admin/Staff only
  getMyEventBookings: (params) => api.get('/my-event-bookings', { params }), // Customer's own event bookings
  getBookedDates: () => api.get('/event-bookings/booked-dates'), // Get all booked dates for calendar
  getById: (id) => api.get(`/event-bookings/${id}`),
  create: (data) => api.post('/event-bookings', data),
  update: (id, data) => api.put(`/event-bookings/${id}`, data),
  updateStatus: (id, status) => api.put(`/event-bookings/${id}`, { status }),
  delete: (id) => api.delete(`/event-bookings/${id}`),
};

// Payments endpoints
export const paymentAPI = {
  getAll: (params) => api.get('/payments', { params }), // Admin/Staff only
  getMyPayments: (params) => api.get('/my-payments', { params }), // Customer's own payments
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
};

// Feedback endpoints
export const feedbackAPI = {
  getAll: (params) => api.get('/feedback', { params }), // Admin/Staff only
  getMyFeedback: (params) => api.get('/my-feedback', { params }), // Customer's own feedback
  getById: (id) => api.get(`/feedback/${id}`),
  create: (data) => api.post('/feedback', data),
  delete: (id) => api.delete(`/feedback/${id}`),
};

// Reports endpoints
export const reportAPI = {
  getSummary: (params) => api.get('/reports/summary', { params }),
  getSales: (params) => api.get('/reports/sales', { params }),
  getBookings: (params) => api.get('/reports/bookings', { params }),
  getTopItems: (params) => api.get('/reports/top-items', { params }),
};

// Dashboard endpoint
export const dashboardAPI = {
  getStats: () => api.get('/dashboard'),
};

// User Management endpoints (Admin only)
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export default api;